// The battle World: owns all entities and orchestrates the fixed-timestep tick.
// Systems live in sibling modules (cover, combat, morale, economy, ai, victory,
// steering) and are pure functions over the World — this file wires them together.

import { NavGrid } from "../engine/grid";
import { Rng } from "../engine/rng";
import { clamp, type Vec2 } from "../engine/vec";
import { BATTLE_MAP, unit } from "./data";
import type {
  Army,
  CoverDef,
  Effect,
  Faction,
  MapDef,
  Model,
  Posture,
  Squad,
  StratPoint,
  Structure,
  WorldConfig,
} from "./types";
import { buildCoverSlots, type CoverSlot } from "./cover";
import { stepEconomy } from "./economy";
import { stepSteering } from "./steering";
import { stepCombat } from "./combat";
import { stepMorale } from "./morale";
import { stepAI } from "./ai";
import { stepVictory } from "./victory";

/** Central tuning knobs (documented in docs/dev/ARCHITECTURE.md). */
export const TUNE = {
  coverSnapRadius: 110, // how far a model will slide to reach cover from its slot
  coverEngageDist: 30, // how close a model must be to a wall to count as behind it
  moveArrive: 10,
  suppressionDecay: 0.22, // per second when not under fire
  suppressionPinned: 0.42, // >= this → pinned (movement penalty)
  suppressionProne: 0.72, // >= this → prone (models on the dirt)
  moraleRegen: 3.2, // per second out of combat
  moraleRegenCombatFactor: 0.25,
  rallyTime: 5.5, // seconds out of fire before a broken squad rallies
  brokenDamageResist: 0.45, // broken/prone models take (1-this) damage
  disciplineGain: 0.09, // Legion doctrine stacks/sec in cover, stationary
  disciplineMax: 1.0,
  disciplineDecay: 0.25, // /sec when moving / out of cover
  fervorPerKill: 0.018,
  fervorPerCharge: 0.012,
  fervorDecay: 0.008, // /sec
  meleeIntoFirePenalty: 0.15, // accuracy multiplier when shooting into a melee
  movingAccuracy: 0.55, // accuracy multiplier while the shooter is moving
  suppressedAccuracy: 0.5, // multiplier at full suppression
};

let nextSquadId = 1;
let nextModelId = 1;
let nextStructId = 1;

export interface WorldStats {
  ticks: number;
  moraleBreaks: number;
  frenzies: number;
  kills: number;
  captures: number;
  squadsWiped: number;
}

export class World {
  cfg: WorldConfig;
  map: MapDef;
  grid: NavGrid;
  rng: Rng;
  time = 0; // seconds
  tickCount = 0;

  squads: Squad[] = [];
  structures: Structure[] = [];
  points: StratPoint[] = [];
  cover: CoverDef[] = [];
  coverSlots: CoverSlot[] = [];
  effects: Effect[] = [];
  armies: Record<Faction, Army>;

  modelOwner = new Map<number, Squad>();
  modelById = new Map<number, Model>();

  phase: "playing" | "won" | "lost" = "playing";
  winner: Faction | null = null;

  stats: WorldStats = {
    ticks: 0,
    moraleBreaks: 0,
    frenzies: 0,
    kills: 0,
    captures: 0,
    squadsWiped: 0,
  };

  /** Optional per-tick hook (scripted demo / smoke test choreography). */
  onTick: ((w: World, dt: number) => void) | null = null;

  constructor(cfg: WorldConfig) {
    this.cfg = cfg;
    this.map = BATTLE_MAP;
    this.rng = new Rng(cfg.seed);
    this.grid = new NavGrid(this.map.width, this.map.height, this.map.cell);
    this.cover = this.map.cover.map((c) => ({ ...c }));
    this.armies = {
      legion: this.makeArmy("legion", cfg.playerFaction !== "legion"),
      kindled: this.makeArmy("kindled", cfg.playerFaction !== "kindled"),
    };
    this.initTerrain();
    this.initPoints();
    this.coverSlots = buildCoverSlots(this.cover);
    this.initBases();
  }

  private makeArmy(faction: Faction, isAI: boolean): Army {
    return {
      faction,
      isAI,
      req: 320,
      pwr: 45,
      reqRate: 0,
      pwrRate: 0,
      ticket: this.cfg.ticketStart,
      slotCap: this.cfg.slotCap,
      fervor: 0,
      fervorTier: 0,
      queue: [],
      hero: null,
      generatorSlots: this.map.generatorSlots[faction].map((s) => ({ ...s })),
      usedGeneratorSlots: 0,
      hqTier: 1,
    };
  }

  private initTerrain(): void {
    // Solid (non-garrison) buildings block pathing and LOS. Garrison shells are
    // passable heavy cover (GDD §4.4 garrison — light version for the slice).
    for (const c of this.cover) {
      if (c.kind === "building" && !c.garrison) {
        this.grid.setBlockedRect(c.x!, c.y!, c.w!, c.h!, true);
      }
    }
  }

  private initPoints(): void {
    for (const p of this.map.points) {
      this.points.push({
        id: p.id,
        name: p.name,
        pos: { x: p.x, y: p.y },
        value: p.value,
        captureRadius: 130,
        isPower: !!p.isPower,
        owner: "neutral",
        capturing: "neutral",
        captureProgress: 0,
        contested: false,
        hasOutpost: false,
      });
    }
  }

  private initBases(): void {
    for (const f of ["legion", "kindled"] as Faction[]) {
      const hqPos = this.map.hq[f];
      this.spawnStructure(f, "hq", hqPos);
      // Each side opens with a barracks + one generator online.
      const gen = this.armies[f].generatorSlots[0];
      this.spawnStructure(f, "generator", gen);
      this.armies[f].usedGeneratorSlots = 1;
      const bx = f === "legion" ? hqPos.x + 120 : hqPos.x - 120;
      this.spawnStructure(f, "barracks", { x: bx, y: hqPos.y + 120 });
    }
  }

  // ---- entity construction -------------------------------------------------

  spawnStructure(faction: Faction, kind: Structure["kind"], pos: Vec2, instant = true): Structure {
    const hpByKind: Record<string, number> = {
      hq: 3200,
      barracks: 900,
      generator: 600,
      turret: 700,
      outpost: 500,
    };
    const radiusByKind: Record<string, number> = {
      hq: 48,
      barracks: 34,
      generator: 24,
      turret: 20,
      outpost: 22,
    };
    const s: Structure = {
      id: nextStructId++,
      faction,
      kind,
      pos: { ...pos },
      hp: hpByKind[kind],
      maxHp: hpByKind[kind],
      radius: radiusByKind[kind],
      tier: 1,
      weaponCd: 0,
      buildProgress: instant ? 1 : 0,
    };
    if (kind === "turret") {
      s.weapon = {
        name: "Turret Gun",
        range: 380,
        damage: 14,
        cooldown: 0.5,
        accuracy: 0.7,
        suppression: 0.02,
        moraleDamage: 0.8,
        burst: 1,
        tracerColor: faction === "legion" ? "#ffd27a" : "#ff8a3c",
      };
    }
    if (kind !== "outpost") this.grid.setBlockedRect(pos.x - s.radius, pos.y - s.radius, s.radius * 2, s.radius * 2, true);
    this.structures.push(s);
    return s;
  }

  createSquad(faction: Faction, defId: string, pos: Vec2, opts?: { sergeant?: boolean; launchers?: boolean }): Squad {
    const def = unit(defId);
    const sq: Squad = {
      id: nextSquadId++,
      faction,
      def,
      models: [],
      center: { ...pos },
      facing: faction === "legion" ? 0 : Math.PI,
      morale: def.maxMorale,
      maxMorale: def.maxMorale,
      broken: false,
      frenzy: false,
      rallyTimer: 0,
      suppression: 0,
      discipline: 0,
      auraAcc: 0,
      auraResist: 0,
      inspireTimer: 0,
      chargeBuffTimer: 0,
      stance: "aggressive",
      order: { type: "stop", pos: null, facing: null, targetSquad: -1, targetPoint: -1 },
      path: [],
      pathTarget: null,
      repathTimer: 0,
      meleeLock: new Set(),
      upgrades: [],
      hasSergeant: false,
      launcherModels: 0,
      abilityCd: {},
      selected: false,
      controlGroup: -1,
      timeSinceHit: 99,
      lastAttacker: -1,
      spawnTime: this.time,
      targetSquadCache: -1,
      reinforceQueue: 0,
      reinforceTimer: 0,
      dead: false,
    };
    const count = def.modelCount;
    const offs = formationOffsets(count, def.role === "vehicle" || def.role === "hero" ? 0 : 24);
    for (let i = 0; i < count; i++) {
      this.addModel(sq, addRot(pos, offs[i], sq.facing), i);
    }
    if (def.abilities) for (const ab of def.abilities) sq.abilityCd[ab.id] = 0;
    if (opts?.sergeant) this.applySergeant(sq);
    if (opts?.launchers) this.applyLaunchers(sq);
    this.squads.push(sq);
    return sq;
  }

  addModel(sq: Squad, pos: Vec2, slotIndex: number): Model {
    const m: Model = {
      id: nextModelId++,
      alive: true,
      pos: { ...pos },
      prevPos: { ...pos },
      vel: { x: 0, y: 0 },
      facing: sq.facing,
      prevFacing: sq.facing,
      hp: sq.def.modelHealth,
      maxHp: sq.def.modelHealth,
      posture: "idle",
      coverValue: 1,
      coverDir: null,
      coverTarget: null,
      onRoad: false,
      slotIndex,
      targetModel: -1,
      weaponCd: this.rng.range(0, sq.def.weapon.cooldown),
      aimTime: 0,
      meleeTarget: -1,
      isSergeant: false,
      bob: this.rng.range(0, Math.PI * 2),
      muzzleFlash: 0,
      hitFlash: 0,
    };
    sq.models.push(m);
    this.modelOwner.set(m.id, sq);
    this.modelById.set(m.id, m);
    return m;
  }

  applySergeant(sq: Squad): void {
    if (sq.hasSergeant) return;
    sq.hasSergeant = true;
    if (!sq.upgrades.includes("sergeant")) sq.upgrades.push("sergeant");
    // Promote an existing model or add one; sergeants are morale/melee anchors.
    let sgt = sq.models.find((m) => m.alive);
    if (!sgt) return;
    sgt.isSergeant = true;
    sgt.maxHp = Math.round(sq.def.modelHealth * 1.3);
    sgt.hp = sgt.maxHp;
    sq.maxMorale = Math.round(sq.def.maxMorale * 1.2);
    sq.morale = Math.min(sq.maxMorale, sq.morale + 20);
  }

  applyLaunchers(sq: Squad): void {
    const up = sq.def.upgrades?.find((u) => u.id === "launchers" || u.id === "brands");
    if (!up || !up.weapon) return;
    if (!sq.upgrades.includes(up.id)) sq.upgrades.push(up.id);
    const n = up.modelsAffected ?? 2;
    let applied = 0;
    for (const m of sq.models) {
      if (!m.alive || m.isSergeant) continue;
      if (applied >= n) break;
      m.weaponOverride = up.weapon;
      applied++;
    }
    sq.launcherModels = applied;
  }

  /** Reinforce one lost model near HQ/rally, preserving sergeant/launcher loadout. */
  reinforceModel(sq: Squad): void {
    const alive = sq.models.filter((m) => m.alive).length;
    if (alive >= sq.def.modelCount) return;
    const anchor = sq.center;
    const m = this.addModel(sq, { x: anchor.x + this.rng.range(-20, 20), y: anchor.y + this.rng.range(-20, 20) }, sq.models.length);
    // restore launcher loadout if short
    const launchersWanted = sq.launcherModels;
    const launchersNow = sq.models.filter((x) => x.alive && x.weaponOverride).length;
    if (launchersNow < launchersWanted) {
      const up = sq.def.upgrades?.find((u) => u.id === "launchers" || u.id === "brands");
      if (up?.weapon) m.weaponOverride = up.weapon;
    }
    if (sq.hasSergeant && !sq.models.some((x) => x.alive && x.isSergeant)) {
      m.isSergeant = true;
      m.maxHp = Math.round(sq.def.modelHealth * 1.3);
      m.hp = m.maxHp;
    }
  }

  addEffect(e: Effect): void {
    this.effects.push(e);
    if (this.effects.length > 900) this.effects.splice(0, this.effects.length - 900);
  }

  // ---- queries -------------------------------------------------------------

  enemyOf(f: Faction): Faction {
    return f === "legion" ? "kindled" : "legion";
  }

  aliveSquads(f?: Faction): Squad[] {
    return this.squads.filter((s) => !s.dead && (f === undefined || s.faction === f));
  }

  structuresOf(f: Faction): Structure[] {
    return this.structures.filter((s) => s.faction === f && s.hp > 0);
  }

  hqOf(f: Faction): Structure | undefined {
    return this.structures.find((s) => s.faction === f && s.kind === "hq" && s.hp > 0);
  }

  /** Nearest living enemy squad to a point (optionally within range). */
  nearestEnemySquad(f: Faction, from: Vec2, maxDist = Infinity): Squad | null {
    let best: Squad | null = null;
    let bd = maxDist * maxDist;
    for (const s of this.squads) {
      if (s.dead || s.faction === f) continue;
      const d = (s.center.x - from.x) ** 2 + (s.center.y - from.y) ** 2;
      if (d < bd) {
        bd = d;
        best = s;
      }
    }
    return best;
  }

  aliveModels(sq: Squad): Model[] {
    return sq.models.filter((m) => m.alive);
  }

  setPosture(m: Model, p: Posture): void {
    m.posture = p;
  }

  // ---- main tick -----------------------------------------------------------

  tick(dt: number): void {
    if (this.phase !== "playing") {
      // keep effects animating on the end screen
      this.updateEffects(dt);
      return;
    }
    this.time += dt;
    this.tickCount++;
    this.stats.ticks = this.tickCount;

    // snapshot for interpolation
    for (const sq of this.squads) {
      for (const m of sq.models) {
        m.prevPos.x = m.pos.x;
        m.prevPos.y = m.pos.y;
        m.prevFacing = m.facing;
      }
    }

    if (this.onTick) this.onTick(this, dt);

    stepEconomy(this, dt);
    stepAI(this, dt);
    stepSteering(this, dt);
    stepCombat(this, dt);
    stepMorale(this, dt);
    stepVictory(this, dt);

    this.cleanup();
    this.updateEffects(dt);
  }

  private cleanup(): void {
    for (const sq of this.squads) {
      if (sq.dead) continue;
      // reap dead models
      for (const m of sq.models) {
        if (!m.alive && this.modelById.has(m.id)) {
          this.modelById.delete(m.id);
          this.modelOwner.delete(m.id);
        }
      }
      const aliveCount = sq.models.filter((m) => m.alive).length;
      if (aliveCount === 0 && sq.reinforceQueue <= 0) {
        sq.dead = true;
        this.stats.squadsWiped++;
      }
    }
    // The squad center is the AUTHORITATIVE movement anchor (advanced by steering);
    // models chase formation slots off it. We only tether it back toward the live
    // models when they drift too far (blocked/melee) — never hard-snap, or forward
    // motion would be erased before the models can follow.
    for (const sq of this.squads) {
      if (sq.dead) continue;
      let cx = 0;
      let cy = 0;
      let n = 0;
      for (const m of sq.models) {
        if (!m.alive) continue;
        cx += m.pos.x;
        cy += m.pos.y;
        n++;
      }
      if (n === 0) continue;
      const gx = cx / n;
      const gy = cy / n;
      const dx = gx - sq.center.x;
      const dy = gy - sq.center.y;
      const d = Math.hypot(dx, dy);
      const tether = 46;
      if (d > tether) {
        const pull = (d - tether) / d;
        sq.center.x += dx * pull;
        sq.center.y += dy * pull;
      }
    }
    // drop wiped structures from nav (leave rubble passable)
    for (const s of this.structures) {
      if (s.hp <= 0 && !(s as any)._reaped) {
        (s as any)._reaped = true;
        if (s.kind !== "outpost") this.grid.setBlockedRect(s.pos.x - s.radius, s.pos.y - s.radius, s.radius * 2, s.radius * 2, false);
      }
    }
    this.structures = this.structures.filter((s) => s.hp > 0);
    // fully purge squads that are dead and have no runners for many ticks
    this.squads = this.squads.filter((s) => !(s.dead && s.models.every((m) => !m.alive)));
  }

  private updateEffects(dt: number): void {
    for (const e of this.effects) e.t += dt;
    this.effects = this.effects.filter((e) => e.t < e.ttl);
  }

  /** Current strategic-point tally (for HUD + victory). */
  pointTally(): Record<Faction | "neutral", number> {
    const t = { legion: 0, kindled: 0, neutral: 0 } as Record<Faction | "neutral", number>;
    for (const p of this.points) t[p.owner]++;
    return t;
  }

  slotsUsed(f: Faction): number {
    let n = 0;
    for (const s of this.squads) if (!s.dead && s.faction === f) n += s.def.slots;
    return n;
  }
}

// ---- helpers ---------------------------------------------------------------

/** Local-space formation offsets: up to 5 wide, ranked back. */
export function formationOffsets(n: number, spacing: number): Vec2[] {
  if (n <= 1) return [{ x: 0, y: 0 }];
  const perRow = Math.min(5, Math.max(3, Math.ceil(Math.sqrt(n))));
  const offs: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const row = Math.floor(i / perRow);
    const col = i % perRow;
    const rowCount = Math.min(perRow, n - row * perRow);
    const lateral = (col - (rowCount - 1) / 2) * spacing;
    const forward = -row * spacing + ((Math.ceil(n / perRow) - 1) * spacing) / 2;
    offs.push({ x: forward, y: lateral });
  }
  return offs;
}

/** Rotate a local offset by facing and add to a base point. */
export function addRot(base: Vec2, off: Vec2, facing: number): Vec2 {
  const c = Math.cos(facing);
  const s = Math.sin(facing);
  return { x: base.x + off.x * c - off.y * s, y: base.y + off.x * s + off.y * c };
}

export { clamp };
